import { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import Modal from '@/components/Modal';
import { InfosMessage } from '@/components/Message/InfosMessage';

import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import { useGetCompaniesSelectOptions } from '@/hooks/company.hooks';

import {
  IconInfoCircle,
  IconFileTypeCsv,
  IconCloudUp,
} from '@tabler/icons-react';

import { downloadTemplateVehiculeImport } from '@/utils/file.utils';
import { Radio } from '@/components/Controls/Radio';
import { VehiculeOwnerType } from '@/api/entities/vehicule';
import { Select } from '@/components/Controls/Select';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { TFunction } from 'i18next';

type VehiculeImportModalProps = {
  id?: string;
  t: TFunction<undefined>;
  onConfirm?: () => void;
  confirmLabel?: string;
  onFileChange?: (File) => void;
  setVehiculeOwner?: Dispatch<SetStateAction<TVehiculeSchema>>;
};

export const VehiculeImportModal = ({
  id,
  t,
  onConfirm,
  confirmLabel,
  onFileChange,
  setVehiculeOwner,
}: VehiculeImportModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [ownerType, setOwnerType] = useState<VehiculeOwnerType>(null);

  const { getGroupsSelectOptions } = useGetGroupsSelectOptions();
  const { getCompaniesSelectOptions } = useGetCompaniesSelectOptions();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    setFileName(file ? file.name : null);
    if (onFileChange) {
      onFileChange(file);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Modal
      id={id}
      title={t('importVehicules')}
      size="medium"
      secondBtnLabel={confirmLabel}
      showSecondBtn
      onClickSecondBtn={onConfirm}
    >
      <div className="p-4 border-dashed border-primary border-2 rounded-lg mb-6">
        <InfosMessage
          containerClassName="bg-white p-0 mb-4"
          messageClassName="text-black"
          icon={<IconInfoCircle stroke={2} className="text-primary" />}
          message={t('uploadVehiculeFile')}
        />
        <InfosMessage
          containerClassName="bg-white p-0"
          messageClassName="text-black"
          icon={<IconInfoCircle stroke={2} className="text-primary" />}
          message={t('common:infoImport')}
        />
      </div>

      <div className="mb-6 flex gap-2 justify-center">
        <IconFileTypeCsv stroke={2} className="text-primary" />
        <span>
          {t('common:downloadModelImport')}{' '}
          <span
            className="cursor-pointer text-primary"
            onClick={downloadTemplateVehiculeImport}
          >
            {t('common:here')}
          </span>
        </span>
      </div>

      <div className="mb-4">
        <Button
          buttonClassName="m-auto"
          text={t('common:importFile')}
          onClick={handleImportClick}
          leftIcon={<IconCloudUp stroke={2} className="text-white" />}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv, .xlsx, .xls"
          className="hidden"
          onChange={handleFileChange}
        />
        {fileName && (
          <div className="mt-2 text-center text-primary">
            {t('common:fileName')} : {fileName}
          </div>
        )}
      </div>
      {fileName && (
        <div className="mb-4">
          <p className="mb-4 text-center text-gray-700">
            {t('selectVehiclesOwner')}
          </p>
          <Radio
            name="owner-type"
            containerClassName="mb-4"
            radioListClassName="flex flex-row gap-4 m-auto"
            items={[
              {
                displayedText: 'Groupe',
                value: VehiculeOwnerType.GROUP,
              },
              {
                displayedText: 'Société',
                value: VehiculeOwnerType.COMPANY,
              },
            ]}
            onChange={(value) => {
              setOwnerType(value.target.value as unknown as VehiculeOwnerType);
              setVehiculeOwner({
                ownerType: value.target.value as VehiculeOwnerType,
              });
            }}
          />
          <Select
            containerClassName={`w-fit ${ownerType ? '' : 'hidden'} m-auto`}
            options={
              ownerType === VehiculeOwnerType.GROUP
                ? getGroupsSelectOptions()
                : getCompaniesSelectOptions()
            }
            onChange={(e) => {
              if (ownerType === VehiculeOwnerType.GROUP)
                setVehiculeOwner((prev) => ({
                  ...prev,
                  ownerGroupId: e.target.value as VehiculeOwnerType,
                }));
              if (ownerType === VehiculeOwnerType.COMPANY)
                setVehiculeOwner((prev) => ({
                  ...prev,
                  ownerCompanyId: e.target.value as VehiculeOwnerType,
                }));
            }}
          />
        </div>
      )}
    </Modal>
  );
};
